import * as Sentry from '@sentry/react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { userManager } from '../userManager';
import { performStepUpMfa, selectMfaUserId } from '../common/store/mfa';
import { getAppStore, peekAppStore } from '../store/appStore';

const API_BASEURL = process.env.REACT_APP_API_BASEURL;

declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuthOverride?: boolean;
    skipStepUp?: boolean;
  }
}

export interface ApiError {
  name: string | undefined;
  message: string | undefined;
  status: number | undefined;
  statusText: string | undefined;
  stack?: string | undefined;
}

const config: AxiosRequestConfig = {
  baseURL: API_BASEURL,
  timeout: 30000,
};

const instance = () => {
  const result = axios.create(config);
  result.interceptors.response.use(
    (response) => response,
    async (axiosError: AxiosError) => {
      const originalRequest = axiosError.config as AxiosRequestConfig & { _stepUpRetried?: boolean };
      if (
        axiosError.response?.status === 403 &&
        (axiosError.response.data as Record<string, unknown>)?.error === 'step_up_required' &&
        !originalRequest.skipStepUp &&
        !originalRequest._stepUpRetried
      ) {
        originalRequest._stepUpRetried = true;
        const appStore = getAppStore();
        try {
          await appStore.dispatch(performStepUpMfa({ service: mfaServiceFromUrl(originalRequest.url), actionType: 'window' })).unwrap();
          return result(originalRequest);
        } catch {
          return errorHandler(axiosError);
        }
      }
      return errorHandler(axiosError);
    }
  );
  result.interceptors.request.use(
    function (config) {
      return Promise.resolve<AxiosRequestConfig>(
        userManager.getUser().then((user) => {
          if (!config.skipAuthOverride) {
            if (user && !user.expired && config.headers) {
              config.headers['Authorization'] = 'Bearer ' + user.access_token;
            } else if (config.headers) {
              config.headers['Authorization'] = '';
            }
          }

          const appStore = peekAppStore();
          const mfaUserId = appStore ? selectMfaUserId(appStore.getState()) : null;
          if (mfaUserId && config.headers) {
            config.headers['X-Mfa-User-Id'] = mfaUserId;
          }

          const correlationId = uuidv4();
          if (config.headers) {
            config.headers['X-Correlation-Id'] = correlationId;
          }

          Sentry.configureScope((scope) => {
            scope.setTag('correlation_id', correlationId);
          });

          return config;
        })
      );
    },
    function (error) {
      return Promise.reject(error);
    }
  );
  return result;
};

const errorHandler = async (axiosError: AxiosError) => {
  let data = axiosError.response?.data;

  if (data instanceof Blob && data.type === 'application/json') {
    try {
      data = JSON.parse(await data.text());
    } catch {
      data = undefined;
    }
  }

  const wrappedErrors =
    (data as Record<string, unknown>)?.detail ||
    (!!(data as Record<string, unknown>)?.error && (data as Record<string, unknown>)?.error instanceof Array
      ? ((data as Record<string, unknown>).error as string[]).join()
      : '');

  const error: ApiError = {
    name: !!wrappedErrors ? 'wrapped-error' : axiosError.name,
    message: (wrappedErrors as string) || axiosError.message,
    status: axiosError.response?.status,
    statusText: axiosError.response?.statusText,
    stack: axiosError.stack,
  };

  // no status means network error
  if (!error.status) {
    error.status = 599;
    error.statusText = 'Network Error';
  }

  return Promise.reject(error);
};

const mfaServiceFromUrl = (url?: string): string => (url || '').replace(/^\/+/, '').split(/[/?#]/)[0];

export default instance();
